import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useTheme } from "../../contexts/ThemeContext";
import { useDeviceWebSocket } from "../../contexts/WebSocketContext";
import { fetchDeviceList } from "../../services/deviceService";
import { getItem, logout } from "../../utils/authStorage";
import { ROLES } from "../../utils/helpers";
import CustomSelect from "../shared/CustomSelect";
import Header from "../shared/Header";
import {
    TablerAlertTriangle,
    TablerDrone,
    TablerOffline,
    TablerSearch,
    TablerSetting,
    TablerX
} from "../shared/Icons";
import LoadingSpinner from "../shared/LoadingSpinner";
import { DEVICE_STATUS_FILTER_OPTIONS, DEVICE_TYPE_FILTER_OPTIONS } from "../shared/selectOptions";
import Sidebar from "../shared/Sidebar";
import "./DeviceList.css";

const OnlineIcon = () => (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '6px', flexShrink: 0 }}>
        <path d="M20 6L9 17l-5-5" />
    </svg>
);

function DeviceList() {
    const { t } = useTranslation();
    const [devices, setDevices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [sortOrder, setSortOrder] = useState("desc");
    const [collapsed, setCollapsed] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [searchTerm, setSearchTerm] = useState("");
    const [selectedType, setSelectedType] = useState("All");
    const [selectedStatus, setSelectedStatus] = useState("All");
    const [isBannerVisible, setIsBannerVisible] = useState(true);

    const devicesPerPage = 10;
    const { subscribe } = useDeviceWebSocket();
    const navigate = useNavigate();
    const { isDarkMode } = useTheme();

    const username = getItem("username");
    const role = (getItem("role") || "operator").toLowerCase();

    const handleSignOut = () => {
        logout();
    };

    const hasRoleFn = (allowedRoles) => {
        const storedRole = (getItem("role") || '').toLowerCase();
        return allowedRoles.flat().some(r => typeof r === 'string' && r.toLowerCase() === storedRole);
    };

    const showSettings = hasRoleFn(ROLES.ADMIN_ONLY);

    useEffect(() => {
        async function loadDevices() {
            try {
                const data = await fetchDeviceList();
                const sorted = sortByUpdatedAt(data, "desc");
                setDevices(sorted);
            } catch (err) {
                console.error("Failed to load devices:", err);
            } finally {
                setLoading(false);
            }
        }
        loadDevices();
    }, []);

    useEffect(() => {
        const unsub = subscribe((raw) => {
            const type = raw?.type;
            const data = raw?.message || raw?.payload || raw?.data;
            if (type === "device.drone.battery") {
                const deviceId = data?.device_id;
                const batteryPercentage = data?.battery_percentage;
                if (deviceId && batteryPercentage !== undefined) {
                    setDevices((prev) =>
                        prev.map((d) =>
                            d.id === deviceId ? { ...d, battery_percentage: batteryPercentage } : d
                        )
                    );
                }
                return;
            }
            if (type === "device.status.active" || type === "device.status.inactive" || type === "device.status.streaming") {
                window.location.reload();
                return;
            }
        });
        return unsub;
    }, [subscribe]);

    const sortByUpdatedAt = (list, order) => {
        return [...list].sort((a, b) => {
            const timeA = new Date(a.updated_at).getTime();
            const timeB = new Date(b.updated_at).getTime();
            return order === "asc" ? timeA - timeB : timeB - timeA;
        });
    };

    const toggleSortOrder = () => {
        const newOrder = sortOrder === "asc" ? "desc" : "asc";
        setSortOrder(newOrder);
        setDevices(sortByUpdatedAt(devices, newOrder));
    };

    // Format date as DD/M/YYYY H:MM AM/PM
    const formatDate = (isoString) => {
        if (!isoString) return "—";
        const date = new Date(isoString);
        const day = date.getDate();
        const month = date.getMonth() + 1;
        const year = date.getFullYear();
        
        let hours = date.getHours();
        const minutes = date.getMinutes();
        const ampm = hours >= 12 ? 'PM' : 'AM';
        hours = hours % 12;
        hours = hours ? hours : 12;
        const minutesStr = minutes < 10 ? '0' + minutes : minutes;
        
        return `${day}/${month}/${year} ${hours}:${minutesStr} ${ampm}`;
    };



    // Filter devices
    const filteredDevices = devices.filter((device) => {
        const matchesSearch = device.name.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesType = selectedType === "All" || device.type === selectedType;
        
        let matchesStatus = true;
        if (selectedStatus !== "All") {
            const isActive = device.status === "ACTIVE" || device.status === "STREAMING";
            if (selectedStatus === "Active") {
                matchesStatus = isActive;
            } else if (selectedStatus === "Inactive") {
                matchesStatus = !isActive;
            }
        }
        return matchesSearch && matchesType && matchesStatus;
    });

    const totalDevices = devices.length;
    const onlineDevices = devices.filter(
        (d) => d.status === "ACTIVE" || d.status === "STREAMING"
    ).length;
    const offlineDevices = totalDevices - onlineDevices;

    const indexOfLastDevice = currentPage * devicesPerPage;
    const indexOfFirstDevice = indexOfLastDevice - devicesPerPage;
    const currentDevices = filteredDevices.slice(indexOfFirstDevice, indexOfLastDevice);
    const totalPages = Math.ceil(filteredDevices.length / devicesPerPage);

    return (
        <div className={`device-list-page ${isDarkMode ? "dark" : "light"}`}>
            <Sidebar
                collapsed={collapsed}
                onToggleCollapse={() => setCollapsed(prev => !prev)}
                isDarkMode={isDarkMode}
                username={username}
                role={role}
                onLogout={handleSignOut}
                hasRole={hasRoleFn}
            />

            <div className="main-content">
                {/* Header matching DashboardPage layout */}
                <Header title={t("nav.deviceList")} />
                <div className="main-divider" />

                <div className="device-list-container">
                    <div className="device-list-title-row">
                        <div className="title-area">
                            <h3 className="device-list-title">{t("label.devices")}</h3>
                            <p className="device-list-subtitle">
                                {t("label.devicesSubtitle")}
                            </p>
                        </div>
                        {showSettings && (
                            <button
                                className="add-device-btn"
                                onClick={() => navigate("/device-order")}
                            >
                                <span className="plus-icon">+</span> {t("button.addDevice")}
                            </button>
                        )}
                    </div>

                    {/* Dismissable alert banner if all devices are offline */}
                    {onlineDevices === 0 && totalDevices > 0 && isBannerVisible && (
                        <div className="devices-alert-banner">
                            <div className="alert-content-wrapper" style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                <span className="alert-banner-icon">
                                    <TablerAlertTriangle color="var(--alert-text)" size={16} />
                                </span>
                                <span className="alert-banner-text">
                                    <strong>{t("label.allDevicesOffline", { count: totalDevices })}</strong>
                                </span>
                            </div>
                            <button className="alert-close-btn" onClick={() => setIsBannerVisible(false)}>
                                <TablerX size={14} color="var(--alert-text)" />
                            </button>
                        </div>
                    )}

                    {/* Summary Metrics Cards */}
                    <div className="devices-summary-cards">
                        <div className="summary-card">
                            <span className="summary-card-label">{t("label.totalDevice")}</span>
                            <span className="summary-card-value">{totalDevices}</span>
                        </div>
                        <div className="summary-card">
                            <span className="summary-card-label">{t("status.online")}</span>
                            <span className="summary-card-value online">{onlineDevices}</span>
                        </div>
                        <div className="summary-card">
                            <span className="summary-card-label">{t("status.offline")}</span>
                            <span className="summary-card-value offline">{offlineDevices}</span>
                        </div>
                    </div>

                    {/* Search / Filter Row with Dropdowns */}
                    <div className="filter-controls-row">
                        <div className="search-input-wrapper">
                            <TablerSearch className="search-icon" size={16} color="var(--text-muted)" />
                            <input
                                type="text"
                                className="search-input"
                                placeholder={t("label.searchDeviceName")}
                                value={searchTerm}
                                onChange={(e) => {
                                    setSearchTerm(e.target.value);
                                    setCurrentPage(1);
                                }}
                            />
                        </div>

                        <div className="filter-dropdowns-group">
                            <CustomSelect
                                value={selectedType}
                                onChange={(val) => {
                                    setSelectedType(val);
                                    setCurrentPage(1);
                                }}
                                options={DEVICE_TYPE_FILTER_OPTIONS.map(o => ({ ...o, label: t(o.labelKey) }))}
                            />

                            <CustomSelect
                                value={selectedStatus}
                                onChange={(val) => {
                                    setSelectedStatus(val);
                                    setCurrentPage(1);
                                }}
                                options={DEVICE_STATUS_FILTER_OPTIONS.map(o => ({ ...o, label: t(o.labelKey) }))}
                            />
                        </div>
                    </div>

                    {/* Table Container */}
                    <div className="table-responsive">
                        {loading ? (
                            <LoadingSpinner message={t("loading.deviceList")} />
                        ) : filteredDevices.length === 0 ? (
                            <div className="devices-no-records">
                                <h2>{t("label.noDevicesFound")}</h2>
                            </div>
                        ) : (
                            <table className={`devicelist-table ${!showSettings ? "no-settings" : ""}`}>
                                <colgroup>
                                  <col style={{width: '16.6667%'}} />
                                  <col style={{width: '16.6667%'}} />
                                  <col style={{width: '16.6667%'}} />
                                  <col style={{width: '16.6667%'}} />
                                  <col style={{width: '16.6667%'}} />
                                  <col style={{width: '16.6667%'}} />
                                </colgroup>
                                <thead>
                                    <tr>
                                        <th className="name-col">{t("label.name")}</th>
                                        <th className="type-col">{t("label.type")}</th>
                                        <th className="status-col">{t("label.statusLabel")}</th>
                                        <th className="battery-col">{t("label.battery")}</th>
                                        <th className="last-updated-col" onClick={toggleSortOrder} style={{ cursor: "pointer" }}>
                                            {t("label.lastUpdated")} {sortOrder === "asc" ? "↑" : "↓"}
                                        </th>
                                        {showSettings && <th className="settings-col-header">{t("label.action")}</th>}
                                    </tr>
                                </thead>
                                <tbody>
                                    {currentDevices.map((device) => {
                                        const isOnline = device.status === "ACTIVE" || device.status === "STREAMING";
                                        const isCreated = device.status === "CREATED";
                                        return (
                                            <tr key={device.id}>
                                                <td className="name-col">
                                                    <span
                                                        className="device-link"
                                                        onClick={() => navigate(`/device/${device.id}`)}
                                                    >
                                                        {device.name}
                                                    </span>
                                                </td>
                                                <td className="type-col">{device.type}</td>
                                                <td className="status-col">
                                                    <span
                                                        className={`status-badge ${
                                                            isOnline
                                                                ? "status-active"
                                                                : isCreated
                                                                ? "status-created"
                                                                : "status-inactive"
                                                        }`}
                                                    >
                                                        {isOnline ? (
                                                            <OnlineIcon />
                                                        ) : isCreated ? (
                                                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '6px', flexShrink: 0 }}>
                                                                <circle cx="12" cy="12" r="10" />
                                                            </svg>
                                                        ) : (
                                                            <TablerOffline size={18} style={{ marginRight: '6px', flexShrink: 0 }} />
                                                        )}
                                                        {isOnline
                                                            ? t("status.active")
                                                            : isCreated
                                                            ? t("status.created")
                                                            : t("status.offline")}
                                                    </span>
                                                </td>
                                                <td className="battery-col">
                                                    {device.type === "Drone" && device.battery_percentage !== undefined ? `${device.battery_percentage}%` : '—'}
                                                </td>
                                                <td className="last-updated-cell">{formatDate(device.updated_at)}</td>
                                                {showSettings && (
                                                    <td className="settings-action-cell">
                                                        {!isCreated && device.type === "Drone" && (
                                                            <button
                                                                className="settings-action-btn"
                                                                onClick={() =>
                                                                    navigate(
                                                                        `/device/waypoints/${device.id}?name=${encodeURIComponent(
                                                                            device.name
                                                                        )}`
                                                                    )
                                                                }
                                                                title={t("label.setWaypoints")}
                                                            >
                                                                <TablerDrone className="settings-btn-icon" size={16} />
                                                            </button>
                                                        )}
                                                        {!isCreated && device.type !== "Drone" && (isOnline || device.type === "Camera") && (
                                                            <button
                                                                className="settings-action-btn"
                                                                onClick={() =>
                                                                    navigate(
                                                                        `/device/configuration/${device.id}?name=${encodeURIComponent(
                                                                            device.name
                                                                        )}`
                                                                    )
                                                                }
                                                                title={t("label.configureDevice")}
                                                            >
                                                                <TablerSetting className="settings-btn-icon" size={16} />
                                                            </button>
                                                        )}
                                                    </td>
                                                )}
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        )}
                    </div>

                    {/* Pagination */}
                    {totalPages > 1 && (
                        <div className="devices-pagination">
                            <button
                                className="pagination-btn"
                                onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                                disabled={currentPage === 1}
                            >
                                {t("button.prev")}
                            </button>
                            <span className="pagination-text">
                                {t("label.pageOf", { current: currentPage, total: totalPages })}
                            </span>
                            <button
                                className="pagination-btn"
                                onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
                                disabled={currentPage === totalPages}
                            >
                                {t("button.next")}
                            </button>
                        </div>
                    )}

                    <div className="device-list-footer">
                        {t("label.showingDevices", { count: filteredDevices.length, total: devices.length })}
                    </div>
                </div>
            </div>
        </div>
    );
}

export default DeviceList;